import React, { createContext, ReactNode, useContext, useEffect, useMemo, useReducer, useRef, useSyncExternalStore } from 'react';
import { AnalysisOrder } from './analysisOrder';
import { PowderOrder } from './powderOrder';
import { RawMaterialControl } from './rawMaterialControl';
import { EventReport } from "./eventReport";
import { createReducer } from './reducer';
import { GenericAction } from './actions';
import { StopCoding, StopCodingFilterType } from './stopCoding';

const STOP_CODING_FILTER_STORAGE_KEY = 'stopCodingFilterValue';
const STOP_CODING_FILTER_DISPLAY_STORAGE_KEY = 'stopCodingFilterDisplayValue';
const STOP_CODING_FILTER_TYPE_STORAGE_KEY = 'stopCodingFilterType';
const STOP_CODING_FILTER_TYPES: StopCodingFilterType[] = ['all', 'workcenter', 'group', 'machine'];


//Powder order context
type PowderAction = GenericAction<PowderOrder>;
type PowderContextType = {
    state: PowderOrder;
    dispatch: React.Dispatch<PowderAction>;
};

export const powderReducer = createReducer<PowderOrder>(new PowderOrder());
export const PowderContext = createContext<PowderContextType | undefined>(undefined);

export function PowderProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(powderReducer, new PowderOrder());
    return <PowderContext.Provider value={{ state, dispatch }}>{children}</PowderContext.Provider>;
}
export function usePowder() {
    const context = useContext(PowderContext);
    if (!context) throw new Error('usePowder must be used within PowderProvider');
    return context;
}

type AnalysisOrderAction = GenericAction<AnalysisOrder>;
type AnalysisOrderContextType = {
    state: AnalysisOrder;
    dispatch: React.Dispatch<AnalysisOrderAction>;
};

export const analysisOrderReducer = createReducer<AnalysisOrder>(new AnalysisOrder());
const AnalysisOrderContext = createContext<AnalysisOrderContextType | undefined>(undefined);

export function AnalysisOrderProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(analysisOrderReducer, new AnalysisOrder());
    return <AnalysisOrderContext.Provider value={{ state, dispatch }}>{children}</AnalysisOrderContext.Provider>;
}

export function useAnalysisOrder() {
    const context = useContext(AnalysisOrderContext);
    if (!context) throw new Error('useAnalysisOrder must be used within AnalysisOrderProvider');
    return context;
}

type RawMaterialControlAction = GenericAction<RawMaterialControl>;
type RawMaterialControlContextType = {
    state: RawMaterialControl;
    dispatch: React.Dispatch<RawMaterialControlAction>;
};

export const rawMaterialControlReducer = createReducer<RawMaterialControl>(new RawMaterialControl());

export function RawMaterialProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(rawMaterialControlReducer, new RawMaterialControl());

    return (
        <PowderContext.Provider
            value={{
                state: state as unknown as PowderOrder,
                dispatch: dispatch as unknown as React.Dispatch<PowderAction>,
            }}
        >
            {children}
        </PowderContext.Provider>
    );
}

export function useRawMaterialControl(): RawMaterialControlContextType {
    const context = useContext(PowderContext);
    if (!context) throw new Error('useRawMaterialControl must be used within RawMaterialProvider');

    return {
        state: context.state as RawMaterialControl,
        dispatch: context.dispatch as unknown as React.Dispatch<RawMaterialControlAction>,
    };
}


//Event report context
type EventReportAction = GenericAction<EventReport>;
type EventReportContextType = {
    state: EventReport;
    dispatch: React.Dispatch<EventReportAction>;
};
export const eventReportReducer = createReducer<EventReport>(new EventReport());
const EventReportContext = createContext<EventReportContextType | undefined>(undefined);
export function EventReportProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(eventReportReducer, new EventReport());
    return <EventReportContext.Provider value={{ state, dispatch }}>{children}</EventReportContext.Provider>;
}
export function useEventReport() {
    const context = useContext(EventReportContext);
    if (!context) throw new Error('useEventReport must be used within EventReportProvider');
    return context;
}


type StopCodingAction = GenericAction<StopCoding>;
type StopCodingContextType = {
    state: StopCoding;
    dispatch: React.Dispatch<StopCodingAction>;
};
type StopCodingStore = {
    getState: () => StopCoding;
    subscribe: (listener: () => void) => () => void;
};
export const stopCodingReducer = createReducer<StopCoding>(new StopCoding());
const StopCodingContext = createContext<StopCodingContextType | undefined>(undefined);
const StopCodingStoreContext = createContext<StopCodingStore | undefined>(undefined);
const StopCodingDispatchContext = createContext<React.Dispatch<StopCodingAction> | undefined>(undefined);

function createInitialStopCodingState() {
    const initialState = new StopCoding();

    if (typeof window === 'undefined') {
        return initialState;
    }

    const localStorageStopCodingFilter = window.localStorage.getItem(STOP_CODING_FILTER_STORAGE_KEY);
    const localStorageStopCodingFilterDisplay = window.localStorage.getItem(STOP_CODING_FILTER_DISPLAY_STORAGE_KEY);
    const localStorageStopCodingFilterType = window.localStorage.getItem(STOP_CODING_FILTER_TYPE_STORAGE_KEY);

    if (localStorageStopCodingFilterType && STOP_CODING_FILTER_TYPES.includes(localStorageStopCodingFilterType as StopCodingFilterType)) {
        initialState.stopCodingFilterType = localStorageStopCodingFilterType as StopCodingFilterType;
    }

    if (localStorageStopCodingFilter) {
        initialState.stopCodingFilterValue = localStorageStopCodingFilter;
    }

    if (localStorageStopCodingFilterDisplay) {
        initialState.stopCodingFilterDisplayValue = localStorageStopCodingFilterDisplay;
    } else if (localStorageStopCodingFilter) {
        initialState.stopCodingFilterDisplayValue = localStorageStopCodingFilter;
    }

    return initialState;
}

export function StopCodingProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(stopCodingReducer, undefined, createInitialStopCodingState);
    const stateRef = useRef(state);
    const listenersRef = useRef(new Set<() => void>());

    stateRef.current = state;

    const store = useMemo<StopCodingStore>(() => ({
        getState: () => stateRef.current,
        subscribe: (listener: () => void) => {
            listenersRef.current.add(listener);

            return () => {
                listenersRef.current.delete(listener);
            };
        }
    }), []);

    const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

    useEffect(() => {
        listenersRef.current.forEach((listener) => listener());
    }, [state]);

    return (
        <StopCodingDispatchContext.Provider value={dispatch}>
            <StopCodingStoreContext.Provider value={store}>
                <StopCodingContext.Provider value={contextValue}>{children}</StopCodingContext.Provider>
            </StopCodingStoreContext.Provider>
        </StopCodingDispatchContext.Provider>
    );
}
export function useStopCodingSelector<T>(selector: (state: StopCoding) => T): T {
    const store = useContext(StopCodingStoreContext);
    const cacheRef = useRef<{ selector: (state: StopCoding) => T; state: StopCoding; selection: T } | null>(null);

    if (!store) throw new Error('useStopCodingSelector must be used within StopCodingProvider');

    const getSnapshot = () => {
        const currentState = store.getState();
        const cached = cacheRef.current;

        if (cached && cached.selector === selector && Object.is(cached.state, currentState)) {
            return cached.selection;
        }

        const selection = selector(currentState);
        cacheRef.current = { selector, state: currentState, selection };
        return selection;
    };

    return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}
export function useStopCodingDispatch() {
    const dispatch = useContext(StopCodingDispatchContext);
    if (!dispatch) throw new Error('useStopCodingDispatch must be used within StopCodingProvider');
    return dispatch;
}
export function useStopCoding() {
    const context = useContext(StopCodingContext);
    if (!context) throw new Error('useStopCoding must be used within StopCodingProvider');
    return context;
}




